import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, comics } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryComics = comics.filter((c) => c.category === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto text-center py-20">
            <h1 className="text-2xl font-bold text-foreground-950 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const heroImage = categoryComics.length > 0
    ? categoryComics[0].bannerImage
    : 'https://readdy.ai/api/search-image?query=Colorful%20Korean%20webtoon%20comic%20books%20arranged%20artistically%20on%20wooden%20surface%20soft%20natural%20lighting%20warm%20atmosphere%20creative%20editorial%20photography%20clean%20aesthetic&width=1400&height=600&seq=category-hero-default&orientation=landscape';

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 韩国漫画网`,
    description: `${category.description} - 韩国漫画网免费韩漫大全分类导航`,
    url: `http://rldcigr.com/category/${category.slug}`,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />

      <Navbar />
      <main>
        <section className="relative h-[420px] md:h-[500px] overflow-hidden">
          <img
            src={heroImage}
            alt={`${category.name} - 韩漫分类`}
            title={`${category.name} - 免费韩漫大全分类`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 md:px-6 w-full">
              <div className="max-w-2xl">
                <h1 className="text-4xl md:text-6xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  {category.name}
                </h1>
                <p className="text-base md:text-lg text-white/80">{category.description}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />

            <div className="mt-6 overflow-x-auto pb-2 -mx-1 px-1">
              <div className="flex items-center gap-2 min-w-max">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:text-foreground-950 hover:bg-background-200/70'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm w-4 h-4 flex items-center justify-center`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="pb-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-bold text-foreground-950">
                共 <span className="text-primary-500">{categoryComics.length}</span> 部作品
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">排序：</span>
                <select className="bg-background-100 border border-background-300 rounded-md px-3 py-1.5 text-sm text-foreground-700 outline-none focus:border-primary-500 cursor-pointer">
                  <option>热度最高</option>
                  <option>评分最高</option>
                  <option>最近更新</option>
                </select>
              </div>
            </div>

            {categoryComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                {categoryComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.slug}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative rounded-lg overflow-hidden bg-background-100 mb-3 aspect-[3/4]">
                      <img
                        src={comic.coverImage}
                        alt={comic.title}
                        title={`${comic.title} - ${category.name}韩漫在线阅读`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-accent-500 text-background-50 text-xs font-semibold">
                        ★ {comic.rating}
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1 line-clamp-1 group-hover:text-primary-500 transition-colors">{comic.title}</h3>
                    <div className="flex items-center gap-2 text-xs text-foreground-500">
                      <span>{comic.author}</span>
                      <span>·</span>
                      <span>{comic.status}</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500 mb-4">该分类暂无漫画</p>
                <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">返回首页浏览更多</Link>
              </div>
            )}
          </div>
        </section>

        <section className="py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="lg:w-1/2">
                <h3 className="text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  {category.name}韩漫精选
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  韩国漫画网为您精选{category.name}分类下的优质韩漫资源。我们精心整理每一部作品的信息，
                  让您能够快速找到感兴趣的韩漫并进行在线阅读。无论是热门连载还是经典完结，
                  这里都有您想要的韩漫内容。
                </p>
                <div className="grid grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-3xl md:text-4xl font-bold text-primary-500">+{categoryComics.length}</div>
                    <p className="text-xs text-foreground-500 mt-1">收录作品</p>
                  </div>
                  <div className="text-center border-x border-background-300/60">
                    <div className="text-3xl md:text-4xl font-bold text-accent-500">+{Math.floor(Math.random() * 50 + 10)}万</div>
                    <p className="text-xs text-foreground-500 mt-1">月活跃读者</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl md:text-4xl font-bold text-primary-500">+{Math.floor(Math.random() * 20 + 5)}</div>
                    <p className="text-xs text-foreground-500 mt-1">本月更新</p>
                  </div>
                </div>
              </div>
              <div className="lg:w-1/2 bg-accent-100/80 rounded-lg p-8 flex items-center justify-center min-h-[200px]">
                <div className="text-center">
                  <i className={`${category.icon} text-5xl text-accent-500 mb-4 block`}></i>
                  <p className="text-sm text-foreground-600">
                    点击上方漫画封面<br />开始您的{category.name}韩漫在线阅读之旅
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}